import React, { useState } from "react";

import { getUserFacingApiErrorMessage, useTranslation } from "utils";

import { Flex, IconButton, Message, Modal } from "components";

import { MixNameModal } from "pages/app/crop-mix-composer/components/MixNameModal";
import { ABOVE_SWITCHER_POPOVER_Z_INDEX } from "pages/app/crop-mix-composer/utils/cropMixComposer";

type CropMixRowActionsProps = {
  mixId: string;
  mixName: string;
  isActive: boolean;
  canDelete: boolean;
  takenNames: string[];
  onRename: (name: string) => Promise<void>;
  onDelete: () => Promise<void>;
};

export const CropMixRowActions = ({ mixId, mixName, isActive, canDelete, takenNames, onRename, onDelete }: CropMixRowActionsProps) => {
  const { t } = useTranslation("cropMixComposer");

  const [showRenameModal, setShowRenameModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const handleRename = async (name: string) => {
    await onRename(name);
    setShowRenameModal(false);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onDelete();
      setShowDeleteModal(false);
    } catch (deleteError) {
      Message({ content: getUserFacingApiErrorMessage(deleteError, t("errors.deleteMixFailed")), icon: "error" });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Flex className={`crop-mix-row-actions ${isActive ? "active" : ""}`} gap="0.25rem" onClick={(e) => e.stopPropagation()}>
      <IconButton
        dataTestId={`crop-mix-row-actions-${mixId}-rename`}
        iconName="tabler/edit"
        size="small"
        type="tertiary"
        tooltip={{ title: t("mixActions.rename") }}
        onClick={(e) => {
          e.stopPropagation();
          setShowRenameModal(true);
        }}
      />
      {canDelete ? (
        <IconButton
          className="red-text"
          dataTestId={`crop-mix-row-actions-${mixId}-delete`}
          iconName="tabler/trash"
          size="small"
          type="tertiary"
          tooltip={{ title: t("mixActions.delete") }}
          onClick={(e) => {
            e.stopPropagation();
            setShowDeleteModal(true);
          }}
        />
      ) : null}
      <MixNameModal
        showModal={showRenameModal}
        formName={`cropMixRename-${mixId}`}
        dataTestId="crop-mix-rename-modal"
        title={t("renameMixModal.title")}
        okText={t("renameMixModal.save")}
        submitErrorMessage={t("errors.renameMixFailed")}
        mixName={mixName}
        takenNames={takenNames}
        onSubmit={handleRename}
        onCancel={() => setShowRenameModal(false)}
      />
      <Modal
        className="crop-mix-action-modal"
        dataTestId="crop-mix-delete-modal"
        zIndex={ABOVE_SWITCHER_POPOVER_Z_INDEX}
        showModal={showDeleteModal}
        title={t("deleteMixModal.title")}
        description={t("deleteMixModal.content")}
        okText={t("deleteMixModal.confirm")}
        cancelText={t("deleteMixModal.cancel")}
        confirmLoading={isDeleting}
        onOk={handleDelete}
        onCancel={() => setShowDeleteModal(false)}
      />
    </Flex>
  );
};
